import React from 'react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Separator } from '../ui/separator';
import { User, MapPin, Calendar, Eye, ChevronDown, ChevronUp, Trash2, FileDown } from 'lucide-react';

const SurveyCard = ({ 
  survey, 
  expanded, 
  onToggleExpand, 
  onDelete, 
  onStatusChange, 
  onView,
  onDownloadOrderSheet,
  formatDate, 
  getStatusBadgeColor,
  getWindowTypeLabel 
}) => {
  return (
    <Card className="shadow-lg border-0 bg-white/70 backdrop-blur-sm" data-testid={`survey-card-${survey.id}`}>
      <CardHeader className="pb-4">
        <div className="flex justify-between items-start">
          <div>
            <CardTitle className="text-xl text-gray-900 mb-2 flex items-center gap-2">
              <span>{survey.survey_number}</span>
              <Badge className={getStatusBadgeColor(survey.status)}>
                {survey.status?.replace('_', ' ').toUpperCase()}
              </Badge>
            </CardTitle>
            <div className="flex items-center gap-4 text-sm text-gray-600">
              <span className="flex items-center gap-1">
                <User className="h-4 w-4" />
                {survey.customer_name}
              </span>
              <span className="flex items-center gap-1">
                <MapPin className="h-4 w-4" />
                {survey.customer_postcode || 'No postcode'}
              </span>
              <span className="flex items-center gap-1">
                <Calendar className="h-4 w-4" />
                {formatDate(survey.survey_date || survey.created_at)}
              </span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={onView} data-testid={`view-survey-${survey.id}`}>
              <Eye className="h-4 w-4" />
            </Button>
            <Button variant="ghost" size="sm" onClick={onToggleExpand}>
              {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </Button>
          </div>
        </div>
      </CardHeader>

      {expanded && (
        <CardContent className="pt-0 space-y-4">
          <Separator />
          
          {/* Property Info */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div>
              <span className="font-medium text-gray-600">Property Type:</span>
              <p className="capitalize">{survey.property_type || 'Not specified'}</p>
            </div>
            <div>
              <span className="font-medium text-gray-600">Surveyor:</span>
              <p>{survey.surveyor_name || 'Not assigned'}</p>
            </div>
            <div>
              <span className="font-medium text-gray-600">Total Openings:</span>
              <p>{survey.total_openings || 0}</p>
            </div>
            <div>
              <span className="font-medium text-gray-600">Created:</span>
              <p>{formatDate(survey.created_at)}</p>
            </div>
          </div>

          {/* Openings Summary */}
          {survey.openings && survey.openings.length > 0 && (
            <div className="bg-gray-50 p-4 rounded-lg">
              <h4 className="font-medium text-gray-700 mb-3">Openings Summary:</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {survey.openings.map((opening, index) => (
                  <div key={opening.opening_id || index} className="flex items-center justify-between bg-white p-2 rounded border">
                    <span className="text-sm font-medium">{opening.room_location}</span>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="text-xs">{getWindowTypeLabel(opening.window_type || opening.layout_type)}</Badge>
                      {opening.shutter_style && (
                        <Badge variant="secondary" className="text-xs capitalize">
                          {opening.shutter_style?.replace('_', ' ')}
                        </Badge>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Notes */}
          {survey.general_notes && (
            <div className="bg-yellow-50 p-3 rounded-lg">
              <h4 className="text-sm font-medium text-gray-700 mb-1">General Notes:</h4>
              <p className="text-sm text-gray-600">{survey.general_notes}</p>
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center justify-between pt-4 border-t">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-gray-700">Update Status:</span>
              {['draft', 'in_progress', 'completed', 'cancelled'].map((status) => (
                <Button
                  key={status}
                  size="sm"
                  variant={survey.status === status ? "default" : "outline"}
                  onClick={() => onStatusChange(status)}
                  disabled={survey.status === status}
                  className="text-xs capitalize"
                  data-testid={`status-btn-${status}`}
                >
                  {status.replace('_', ' ')}
                </Button>
              ))}
            </div>
            <div className="flex items-center gap-2">
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => onDownloadOrderSheet?.(survey.id)}
                className="text-xs"
                data-testid={`download-order-sheet-${survey.id}`}
              >
                <FileDown className="h-4 w-4 mr-1" />
                Order Sheet
              </Button>
              <Button 
                variant="destructive" 
                size="sm" 
                onClick={onDelete}
                data-testid={`delete-survey-${survey.id}`}
              >
                <Trash2 className="h-4 w-4 mr-1" />
                Delete
              </Button>
            </div>
          </div>
        </CardContent>
      )}
    </Card>
  );
};

export default SurveyCard;
